import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function CtaSection() {
  const { ref, inView } = useInView();

  return (
    <section id="contact" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Japanese%20elderly%20care%20dayservice%20staff%20smiling%20warmly%20with%20senior%20residents%20bright%20sunny%20garden%20outdoor%20activity%20cherry%20blossom%20trees%20peaceful%20joyful%20atmosphere%20professional%20caregivers%20group%20photo%20natural%20light&width=1920&height=800&seq=cta001&orientation=landscape"
          alt="スタッフと利用者の笑顔"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <div
            className={`inline-flex items-center gap-2 mb-6 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="h-px w-8 bg-[#88B277]" />
            <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Contact Us</span>
          </div>

          <h2
            className={`text-3xl md:text-5xl font-bold text-white leading-tight mb-6 transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            まずは、お気軽に
            <br />
            <span className="text-[#b8d9a8]">ご相談ください。</span>
          </h2>

          <p
            className={`text-white/80 text-base leading-relaxed mb-10 transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            「どんなサービスが使えるの？」「費用はどのくらい？」など、
            どんな小さなご質問でも丁寧にお答えします。
            介護保険の申請方法についてもご案内できます。
          </p>

          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <a
              href="tel:0120-000-000"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#88B277] text-white font-bold text-lg rounded-full hover:bg-[#76a065] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.98-.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              電話で相談する
            </a>
            <div className="flex flex-col justify-center">
              <span className="text-white font-bold text-2xl tracking-wider">0120-000-000</span>
              <span className="text-white/60 text-xs mt-1">受付時間：平日 8:30〜17:30</span>
            </div>
          </div>

          <p
            className={`mt-8 text-white/50 text-xs leading-relaxed transition-all duration-700 delay-500 ${
              inView ? 'opacity-100' : 'opacity-0'
            }`}
          >
            ※ 介護保険サービスのご利用には、要介護・要支援認定が必要です。
            まだ認定を受けていない方も、申請方法からご案内いたします。
          </p>
        </div>
      </div>
    </section>
  );
}
